import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useSessionStore } from "@/stores";
import type IUserLinkedService from "@/models/responses/IUserLinkedService";
import { AxenitaUpsertUserLinkedServiceRequest } from '@/models/requests/UpsertUserLinkedServiceRequest';

export const useUserLinkedServicesStore = defineStore("userLinkedServices", {
  state: () => ({
    services: [] as IUserLinkedService[],
    error: "" as any,
    loadedTenantId: 0,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedTenantId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;

      const loadingTenantId = selectedTenantId.value;
      if (!force && this.loadedTenantId === loadingTenantId) return;

      this.error = "";
      await api
        .get<IUserLinkedService[]>(`v1.0/user-linked-services?tenantId=${loadingTenantId}`)
        .then((response) => {
          this.services = response.data;
          this.loadedTenantId = loadingTenantId;
        })
        .catch(error => {
          if (error.response) {
            // Server responded with an error status code (4xx or 5xx)
            const problem = error.response.data;
      
            if (problem.type === 'https://tools.ietf.org/html/rfc7231#section-6.5.1'
              || problem.type === 'https://tools.ietf.org/html/rfc9110#section-15.6.1'
            ) {
              // invalid operation error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.detail;
            } else {
              // unexpected error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.title + ': ' + problem.detail;
            }
          } else if (error.request) {
            // no response received
            console.error('No response from server:', error.request);
            this.error = 'Server did not respond. Please try again later.';
          } else {
            // other errors
            console.error('Axios error:', error.message);
            this.error = 'Unexpected error: ' + error.message;
          }
        });
    },
    
    async upsert(service: AxenitaUpsertUserLinkedServiceRequest) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      return await api
        .post<IUserLinkedService>("v1.0/user-linked-services", service)
        .then((response) => {
          const index = this.services.findIndex((s) => s.id === response.data.id);
          if (index === -1) {
            this.services.push(response.data);
          } else {
            this.services[index] = response.data;
          }
          return response.data;
        })
        .catch(error => {
          if (error.response) {
            // Server responded with an error status code (4xx or 5xx)
            const problem = error.response.data;
      
            if (problem.type === 'https://tools.ietf.org/html/rfc7231#section-6.5.1'
              || problem.type === 'https://tools.ietf.org/html/rfc9110#section-15.6.1'
            ) {
              // invalid operation error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.detail;
            } else {
              // unexpected error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.title + ': ' + problem.detail;
            }
          } else if (error.request) {
            // no response received
            console.error('No response from server:', error.request);
            this.error = 'Server did not respond. Please try again later.';
          } else {
            // other errors
            console.error('Axios error:', error.message);
            this.error = 'Unexpected error: ' + error.message;
          }
        });
    },
    
    async delete(id: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .delete(`v1.0/user-linked-services/${id}`)
        .then(() => {
          const index = this.services.findIndex((service) => service.id === id);
          if (index !== -1) this.services.splice(index, 1);
        })
        .catch(error => {
          if (error.response) {
            // Server responded with an error status code (4xx or 5xx)
            const problem = error.response.data;
      
            if (problem.type === 'https://tools.ietf.org/html/rfc7231#section-6.5.1'
              || problem.type === 'https://tools.ietf.org/html/rfc9110#section-15.6.1'
            ) {
              // invalid operation error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.detail;
            } else {
              // unexpected error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.title + ': ' + problem.detail;
            }
          } else if (error.request) {
            // no response received
            console.error('No response from server:', error.request);
            this.error = 'Server did not respond. Please try again later.';
          } else {
            // other errors
            console.error('Axios error:', error.message);
            this.error = 'Unexpected error: ' + error.message;
          }
        });
    },
  },
  getters: {
    getById: (state) => (id: number) => {
      return state.services.find((service) => service.id === id);
    },
    getByLinkedServiceId: (state) => (linkedServiceId: string) => {
      return state.services.find((service) => service.linkedServiceId === linkedServiceId);
    },
  },
});
